<template>
  <div class="planner-root" ref="root">

    <!-- ══════════════════════════════════════════
         MOBILE SHEET — Date picker
    ══════════════════════════════════════════ -->
    <transition name="sheet">
      <div v-if="showDateSheet" class="sheet-overlay" @click.self="showDateSheet = false">
        <div class="sheet-panel">
          <div class="sheet-handle"></div>
          <div class="sheet-header">
            <span class="sheet-title">Choisir une date</span>
            <button class="sheet-close" @click="showDateSheet = false">
              <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5"><path d="M18 6L6 18M6 6l12 12"/></svg>
            </button>
          </div>
          <div class="sheet-dp-body">
            <div class="dp-nav">
              <button class="dp-nav-btn" @click="pickerMode === 'months' ? pickerYear-- : prevMonth()">
                <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5"><path d="M15 18l-6-6 6-6"/></svg>
              </button>
              <button class="dp-month-label" @click="pickerMode = pickerMode === 'days' ? 'months' : 'days'">
                <span v-if="pickerMode === 'days'">{{ pickerMonthName }}</span>
                <span v-else>{{ pickerYear }}</span>
                <svg width="11" height="11" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" style="margin-left:4px;opacity:0.4;transition:transform 0.15s" :style="pickerMode==='months'?'transform:rotate(180deg)':''"><path d="M6 9l6 6 6-6"/></svg>
              </button>
              <button class="dp-nav-btn" @click="pickerMode === 'months' ? pickerYear++ : nextMonth()">
                <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5"><path d="M9 18l6-6-6-6"/></svg>
              </button>
            </div>
            <div v-if="pickerMode === 'months'" class="dp-month-grid">
              <button v-for="(mn, mi) in ['Jan','Fév','Mar','Avr','Mai','Jun','Jul','Aoû','Sep','Oct','Nov','Déc']" :key="mi"
                class="dp-month-cell" :class="{ 'dp-month-active': mi === pickerMonth }"
                @click="pickerMonth = mi; pickerMode = 'days'">{{ mn }}</button>
            </div>
            <template v-else>
              <div class="dp-weekdays">
                <span v-for="(d, i) in ['L','M','M','J','V','S','D']" :key="i" class="dp-wd">{{ d }}</span>
              </div>
              <div class="dp-grid">
                <button v-for="(cell, i) in pickerDays" :key="i" class="dp-cell"
                  :class="{ 'dp-empty': !cell, 'dp-selected': cell && cell.date === selectedDate, 'dp-is-today': cell && isTodayDate(cell.date) }"
                  @click="cell && selectDate(cell.date); cell && (showDateSheet = false)">{{ cell ? cell.day : '' }}</button>
              </div>
            </template>
            <button class="dp-today-btn" @click="goToday(); showDateSheet = false" :class="{ active: isToday }">Aujourd'hui</button>
          </div>
        </div>
      </div>
    </transition>

    <!-- ══════════════════════════════════════════
         MOBILE SHEET — Unscheduled events
    ══════════════════════════════════════════ -->
    <transition name="sheet">
      <div v-if="showUnscheduledSheet" class="sheet-overlay" @click.self="showUnscheduledSheet = false">
        <div class="sheet-panel sheet-panel-tall">
          <div class="sheet-handle"></div>
          <div class="sheet-header">
            <span class="sheet-title">À planifier</span>
            <span class="sidebar-badge" style="margin-right:4px">{{ sidebarEvents.length }}</span>
            <button class="sheet-close" @click="showUnscheduledSheet = false">
              <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5"><path d="M18 6L6 18M6 6l12 12"/></svg>
            </button>
          </div>
          <div class="sheet-list">
            <div v-if="loadingUnscheduled" class="list-loader">
              <div class="loader-spinner"></div>
              <span class="loader-label">Chargement…</span>
            </div>
            <template v-else>
              <div v-for="ev in sidebarEvents" :key="'msb-'+ev.id"
                class="ucard" :class="'prio-'+(ev.priority||'normal')"
                @click="mobileScheduleEvent(ev)">
                <div class="ucard-stripe"></div>
                <div class="ucard-body">
                  <div class="ucard-title">{{ ev.title }}</div>
                  <div class="ucard-row" v-if="ev.client">
                    <svg width="10" height="10" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5"><circle cx="12" cy="8" r="4"/><path d="M4 20c0-4 3.6-7 8-7s8 3 8 7"/></svg>
                    {{ ev.client }}
                  </div>
                  <div class="ucard-row" v-if="ev.duration">
                    <svg width="10" height="10" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5"><circle cx="12" cy="12" r="9"/><path d="M12 7v5l3 3"/></svg>
                    {{ formatDuration(ev.duration) }}
                  </div>
                  <div class="ucard-tags" v-if="ev.tags && ev.tags.length">
                    <span class="utag" v-for="t in ev.tags" :key="t">{{ t }}</span>
                  </div>
                </div>
                <div class="ucard-action">
                  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5"><path d="M12 5v14M5 12h14"/></svg>
                </div>
              </div>
              <div v-if="sidebarEvents.length === 0" class="empty-state">
                <svg width="28" height="28" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.5"><path d="M9 11l3 3L22 4"/><path d="M21 12v7a2 2 0 01-2 2H5a2 2 0 01-2-2V5a2 2 0 012-2h11"/></svg>
                <span>Tout est planifié !</span>
              </div>
            </template>
          </div>
        </div>
      </div>
    </transition>

    <!-- ══════════════════════════════════════════
         MOBILE SHEET — Quick schedule
    ══════════════════════════════════════════ -->
    <transition name="sheet">
      <div v-if="mobileScheduleSheet.show" class="sheet-overlay" @click.self="mobileScheduleSheet.show = false">
        <div class="sheet-panel">
          <div class="sheet-handle"></div>
          <div class="sheet-header">
            <span class="sheet-title">Planifier</span>
            <button class="sheet-close" @click="mobileScheduleSheet.show = false">
              <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5"><path d="M18 6L6 18M6 6l12 12"/></svg>
            </button>
          </div>
          <div class="schedule-sheet-content" v-if="mobileScheduleSheet.event">
            <div class="sched-ev-title">{{ mobileScheduleSheet.event.title }}</div>
            <div class="sched-ev-meta" v-if="mobileScheduleSheet.event.client">
              <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5"><circle cx="12" cy="8" r="4"/><path d="M4 20c0-4 3.6-7 8-7s8 3 8 7"/></svg>
              {{ mobileScheduleSheet.event.client }}
            </div>
            <div class="sched-section-label">Technicien</div>
            <div class="sched-tech-list">
              <button v-for="tech in technicians" :key="tech.id" class="sched-tech-btn"
                :class="{ active: mobileScheduleSheet.techId === tech.id }"
                :style="mobileScheduleSheet.techId === tech.id ? { borderColor: tech.color, background: tech.color+'18' } : {}"
                @click="mobileScheduleSheet.techId = tech.id">
                <div class="sched-tech-avatar" :style="{ background: tech.color||'#3b82f6' }">{{ initials(tech.name) }}</div>
                <div class="sched-tech-name">{{ tech.name }}</div>
              </button>
            </div>
            <div class="sched-section-label">Heure de début</div>
            <div class="sched-time-row">
              <select class="sched-select" v-model="mobileScheduleSheet.startH">
                <option v-for="h in visibleHours.slice(0,-1)" :key="h" :value="h">{{ String(h).padStart(2,'0') }}h</option>
              </select>
              <select class="sched-select" v-model="mobileScheduleSheet.startM">
                <option v-for="m in [0,15,30,45]" :key="m" :value="m">:{{ String(m).padStart(2,'0') }}</option>
              </select>
            </div>
            <div class="sched-preview" v-if="mobileScheduleSheet.techId">
              <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><circle cx="12" cy="12" r="9"/><path d="M12 7v5l3 3"/></svg>
              {{ mobileSchedulePreviewLabel }}
            </div>
            <button class="sched-confirm-btn" :disabled="!mobileScheduleSheet.techId" @click="confirmMobileSchedule">
              <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5"><path d="M20 6L9 17l-5-5"/></svg>
              Confirmer
            </button>
          </div>
        </div>
      </div>
    </transition>

    <!-- ══════════════════════════════════════════
         TOP BAR
    ══════════════════════════════════════════ -->
    <header class="top-bar">
      <button v-if="isMobile" class="tb-date-btn" @click="showDateSheet = true">
        <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><rect x="3" y="4" width="18" height="18" rx="2"/><path d="M16 2v4M8 2v4M3 10h18"/></svg>
        <span class="tb-date-label">{{ selectedDateLabel }}</span>
        <svg width="11" height="11" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" style="opacity:0.4"><path d="M6 9l6 6 6-6"/></svg>
      </button>

      <div v-else class="tb-date-static">
        <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><rect x="3" y="4" width="18" height="18" rx="2"/><path d="M16 2v4M8 2v4M3 10h18"/></svg>
        {{ selectedDateLabel }}
      </div>

      <div v-if="isMobile" class="tb-arrows">
        <button class="tb-arrow-btn" @click="offsetDay(-1)">
          <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5"><path d="M15 18l-6-6 6-6"/></svg>
        </button>
        <button class="tb-arrow-btn" @click="offsetDay(1)">
          <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5"><path d="M9 18l6-6-6-6"/></svg>
        </button>
      </div>

      <div class="tb-right">
        <div v-if="isMobile && technicians.length > 1" class="tb-tech-dropdown-wrap" ref="techDropdownWrap">
          <button class="tb-tech-dropdown-btn"
            :style="activeTech ? { borderColor: activeTech.color, color: activeTech.color } : {}"
            @click="toggleTechDropdown">
            <div v-if="activeTech" class="tb-tech-dot" :style="{ background: activeTech.color||'#3b82f6' }"></div>
            <span class="tb-tech-dropdown-name">{{ activeTech ? activeTech.name : 'Technicien' }}</span>
            <svg width="11" height="11" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5"
              :style="showTechDropdown ? 'transform:rotate(180deg)' : ''"
              style="transition:transform 0.15s;opacity:0.5;flex-shrink:0"><path d="M6 9l6 6 6-6"/></svg>
          </button>
        </div>

        <button v-if="isMobile" class="tb-unscheduled-btn" @click="showUnscheduledSheet = true"
          :class="{ 'has-events': sidebarEvents.length > 0 }">
          <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><rect x="3" y="4" width="18" height="18" rx="2"/><path d="M16 2v4M8 2v4M3 10h18M8 14h8M8 18h5"/></svg>
          <span v-if="sidebarEvents.length > 0" class="tb-badge">{{ sidebarEvents.length }}</span>
        </button>

        <button v-if="!isMobile" class="tb-today-btn" @click="goToday" :class="{ active: isToday }">Aujourd'hui</button>
      </div>
    </header>

    <!-- TECH DROPDOWN FLOATING -->
    <transition name="dropdown">
      <div v-if="showTechDropdown && isMobile" class="tb-tech-dropdown-list" :style="dropdownStyle" ref="techDropdownList">
        <button v-for="tech in technicians" :key="tech.id" class="tb-tech-dropdown-item"
          :class="{ active: activeTechId === tech.id }"
          :style="activeTechId === tech.id ? { color: tech.color } : {}"
          @click="activeTechId = tech.id; showTechDropdown = false">
          <div class="tb-tech-dropdown-avatar" :style="{ background: tech.color||'#3b82f6' }">{{ initials(tech.name) }}</div>
          <div class="tb-tech-dropdown-info">
            <span class="tb-tech-dropdown-label">{{ tech.name }}</span>
            <span class="tb-tech-dropdown-role" v-if="tech.role">{{ tech.role }}</span>
          </div>
          <svg v-if="activeTechId === tech.id" width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5" style="margin-left:auto;flex-shrink:0"><path d="M20 6L9 17l-5-5"/></svg>
        </button>
      </div>
    </transition>

    <!-- ══════════════════════════════════════════
         DESKTOP LAYOUT
    ══════════════════════════════════════════ -->
    <div v-if="!isMobile" class="desktop-layout">

      <aside class="sidebar">
        <div class="datepicker">
          <div class="dp-nav">
            <button class="dp-nav-btn" @click="pickerMode === 'months' ? pickerYear-- : prevMonth()">
              <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5"><path d="M15 18l-6-6 6-6"/></svg>
            </button>
            <button class="dp-month-label" @click="pickerMode = pickerMode === 'days' ? 'months' : 'days'">
              <span v-if="pickerMode === 'days'">{{ pickerMonthName }}</span>
              <span v-else>{{ pickerYear }}</span>
              <svg width="10" height="10" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" style="margin-left:3px;opacity:0.4;transition:transform 0.15s" :style="pickerMode==='months'?'transform:rotate(180deg)':''"><path d="M6 9l6 6 6-6"/></svg>
            </button>
            <button class="dp-nav-btn" @click="pickerMode === 'months' ? pickerYear++ : nextMonth()">
              <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5"><path d="M9 18l6-6-6-6"/></svg>
            </button>
          </div>
          <div v-if="pickerMode === 'months'" class="dp-month-grid">
            <button v-for="(mn, mi) in ['Jan','Fév','Mar','Avr','Mai','Jun','Jul','Aoû','Sep','Oct','Nov','Déc']" :key="mi"
              class="dp-month-cell" :class="{ 'dp-month-active': mi === pickerMonth }"
              @click="pickerMonth = mi; pickerMode = 'days'">{{ mn }}</button>
          </div>
          <template v-else>
            <div class="dp-weekdays">
              <span v-for="(d, i) in ['L','M','M','J','V','S','D']" :key="i" class="dp-wd">{{ d }}</span>
            </div>
            <div class="dp-grid">
              <button v-for="(cell, i) in pickerDays" :key="i" class="dp-cell"
                :class="{ 'dp-empty': !cell, 'dp-selected': cell && cell.date === selectedDate, 'dp-is-today': cell && isTodayDate(cell.date) }"
                @click="cell && selectDate(cell.date)">{{ cell ? cell.day : '' }}</button>
            </div>
          </template>
          <button class="dp-today-btn" @click="goToday" :class="{ active: isToday }">Aujourd'hui</button>
        </div>

        <div class="sidebar-header">
          <span class="sidebar-label">À planifier</span>
          <span class="sidebar-badge">{{ sidebarEvents.length }}</span>
        </div>

        <div class="unscheduled-area">
          <div v-if="loadingUnscheduled" class="list-loader">
            <div class="loader-spinner"></div>
            <span class="loader-label">Chargement…</span>
          </div>
          <div v-else class="unscheduled-list">
            <div v-for="ev in sidebarEvents" :key="'sb-'+ev.id"
              class="ucard" :class="'prio-'+(ev.priority||'normal')"
              @mousedown="startDrag($event, ev, 'sidebar')" @click="$emit('event-click', ev)">
              <div class="ucard-stripe"></div>
              <div class="ucard-body">
                <div class="ucard-title">{{ ev.title }}</div>
                <div class="ucard-row" v-if="ev.client">
                  <svg width="10" height="10" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5"><circle cx="12" cy="8" r="4"/><path d="M4 20c0-4 3.6-7 8-7s8 3 8 7"/></svg>
                  {{ ev.client }}
                </div>
                <div class="ucard-row" v-if="ev.duration">
                  <svg width="10" height="10" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5"><circle cx="12" cy="12" r="9"/><path d="M12 7v5l3 3"/></svg>
                  {{ formatDuration(ev.duration) }}
                </div>
                <div class="ucard-row" v-if="ev.address">
                  <svg width="10" height="10" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5"><path d="M12 2C8.13 2 5 5.13 5 9c0 5.25 7 13 7 13s7-7.75 7-13c0-3.87-3.13-7-7-7z"/><circle cx="12" cy="9" r="2.5"/></svg>
                  {{ ev.address }}
                </div>
                <div class="ucard-tags" v-if="ev.tags && ev.tags.length">
                  <span class="utag" v-for="t in ev.tags" :key="t">{{ t }}</span>
                </div>
              </div>
              <svg class="drag-grip" width="12" height="20" viewBox="0 0 12 20" fill="currentColor">
                <circle cx="3" cy="4" r="1.5"/><circle cx="9" cy="4" r="1.5"/>
                <circle cx="3" cy="10" r="1.5"/><circle cx="9" cy="10" r="1.5"/>
                <circle cx="3" cy="16" r="1.5"/><circle cx="9" cy="16" r="1.5"/>
              </svg>
            </div>
            <div v-if="sidebarEvents.length === 0 && pendingCount === 0" class="empty-state">
              <svg width="28" height="28" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.5"><path d="M9 11l3 3L22 4"/><path d="M21 12v7a2 2 0 01-2 2H5a2 2 0 01-2-2V5a2 2 0 012-2h11"/></svg>
              <span>Tout est planifié !</span>
            </div>
          </div>
        </div>
      </aside>

      <!-- TIMELINE (desktop) -->
      <div class="timeline-wrap">
        <div v-if="loadingTimeline" class="timeline-loader">
          <div class="loader-spinner large"></div>
          <span class="loader-label">Chargement du planning…</span>
        </div>
        <div class="tl-corner"><div class="corner-label">Techniciens</div></div>
        <div class="tl-time-head" ref="timeHead">
          <div class="tl-time-inner" :style="{ width: timelineWidth + 'px' }">
            <div v-for="h in visibleHours" :key="'th-'+h" class="time-tick" :style="{ left: timeToX(h,0)+'px' }">
              <span class="time-tick-label">{{ String(h).padStart(2,'0') }}:00</span>
            </div>
            <div v-for="h in visibleHours.slice(0,-1)" :key="'thh-'+h" class="time-tick" :style="{ left: timeToX(h,30)+'px' }">
              <span class="time-tick-label half">{{ String(h).padStart(2,'0') }}:30</span>
            </div>
          </div>
        </div>
        <div class="tl-resource-labels" ref="resourceLabels">
          <div v-for="tech in technicians" :key="'rl-'+tech.id"
            class="resource-label"
            :class="{ 'row-active': dragState.active && dragState.targetTechId === tech.id }"
            :style="{ borderLeftColor: tech.color||'#3b82f6', height: rowHeight+'px' }">
            <div class="tech-avatar" :style="{ background: tech.color||'#3b82f6' }">{{ initials(tech.name) }}</div>
            <div class="tech-meta">
              <div class="tech-name">{{ tech.name }}</div>
              <div class="tech-role" v-if="tech.role">{{ tech.role }}</div>
            </div>
            <div class="tech-count" :style="{ color: tech.color||'#3b82f6' }">{{ techEventCount(tech.id) }}</div>
          </div>
        </div>
        <div class="tl-grid-scroll" ref="gridScroll" @scroll="onGridScroll" @mousedown="onGridMouseDown"
          :class="{ 'is-panning': panState.active, 'is-dragging': dragState.active }">
          <div class="tl-grid-inner" :style="{ width: timelineWidth+'px', height: gridTotalHeight+'px' }">
            <div v-for="h in visibleHours" :key="'vl-'+h" class="v-line" :style="{ left: timeToX(h,0)+'px' }"></div>
            <div v-for="h in visibleHours.slice(0,-1)" :key="'vhh-'+h" class="v-line v-line-half" :style="{ left: timeToX(h,30)+'px' }"></div>
            <div v-for="(tech, i) in technicians" :key="'hs-'+tech.id" class="h-sep" :style="{ top: (i+1)*rowHeight+'px' }"></div>
            <div v-if="nowVisible" class="now-vline" :style="{ left: nowX+'px' }"><div class="now-pip"></div></div>
            <div v-for="(tech, rowIdx) in technicians" :key="'row-'+tech.id"
              class="tech-row"
              :class="{ 'row-drop-active': dragState.active && dragState.targetTechId === tech.id }"
              :style="{ top: rowIdx*rowHeight+'px', height: rowHeight+'px' }">
              <div v-if="dragState.active && dragState.targetTechId === tech.id && dragState.preview"
                class="drop-preview"
                :style="{ left: dragState.preview.left+'px', width: dragState.preview.width+'px', '--pc': tech.color||'#3b82f6' }">
                <span class="preview-label">{{ dragState.preview.startLabel }}–{{ dragState.preview.endLabel }}</span>
              </div>
              <div v-for="ev in techEvents(tech.id)" :key="'ev-'+ev.id"
                class="ev-block"
                :class="{
                  'ev-pending': ev._pending,
                  'ev-dragging': dragState.active && dragState.item && dragState.item.id === ev.id,
                  'popup-below': ev._pending && rowIdx === 0
                }"
                :style="eventStyle(ev, tech.color)"
                @mousedown="ev._pending ? startDrag($event, ev, 'pending') : null"
                @click.stop="!ev._pending && $emit('event-click', ev)">
                <div class="ev-accent" :style="{ background: tech.color||'#3b82f6' }"></div>
                <div class="ev-content">
                  <div class="ev-title">{{ ev.title }}</div>
                  <div class="ev-time">{{ formatTime(ev.startTime) }}–{{ formatTime(ev.endTime) }}</div>
                  <div class="ev-client" v-if="ev.client">{{ ev.client }}</div>
                </div>
                <div v-if="ev._pending" class="ev-actions-popup" @mousedown.stop @click.stop>
                  <button class="ev-btn ev-btn-cancel" title="Annuler" @click="cancelPlacement(ev)">
                    <svg width="10" height="10" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="3"><path d="M18 6L6 18M6 6l12 12"/></svg>
                  </button>
                  <button class="ev-btn ev-btn-confirm" title="Valider" @click="confirmPlacement(ev)">
                    <svg width="10" height="10" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="3"><path d="M20 6L9 17l-5-5"/></svg>
                    <span>Valider</span>
                  </button>
                </div>
                <div v-if="!ev._pending" class="ev-status" :class="'s-'+(ev.status||'planned')"></div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>

    <!-- ══════════════════════════════════════════
         MOBILE LAYOUT
    ══════════════════════════════════════════ -->
    <div v-else class="mobile-layout">
      <div v-if="activeTech" class="mob-tech-bar" :style="{ borderBottomColor: activeTech.color }">
        <div class="mob-tech-avatar" :style="{ background: activeTech.color }">{{ initials(activeTech.name) }}</div>
        <div>
          <div class="mob-tech-name">{{ activeTech.name }}</div>
          <div class="mob-tech-role" v-if="activeTech.role">{{ activeTech.role }}</div>
        </div>
        <div class="mob-tech-count" :style="{ color: activeTech.color }">
          {{ techEventCount(activeTech.id) }} intervention{{ techEventCount(activeTech.id) !== 1 ? 's' : '' }}
        </div>
      </div>

      <div class="mob-timeline-wrap">
        <div v-if="loadingTimeline" class="timeline-loader">
          <div class="loader-spinner large"></div>
          <span class="loader-label">Chargement…</span>
        </div>

        <div class="mob-time-header" ref="mobTimeHead">
          <div class="mob-time-inner" :style="{ width: timelineWidth+'px' }">
            <div v-for="h in visibleHours" :key="'mth-'+h" class="mob-time-tick" :style="{ left: timeToX(h,0)+'px' }">
              {{ String(h).padStart(2,'0') }}:00
            </div>
            <div v-for="h in visibleHours.slice(0,-1)" :key="'mthh-'+h" class="mob-time-tick half" :style="{ left: timeToX(h,30)+'px' }">
              :30
            </div>
          </div>
        </div>

        <div class="mob-grid-scroll" ref="mobGridScroll" @scroll="onMobGridScroll">
          <div class="mob-grid-inner" :style="{ width: timelineWidth+'px', height: mobRowHeight+'px' }">
            <div v-for="h in visibleHours" :key="'mvl-'+h" class="v-line" :style="{ left: timeToX(h,0)+'px' }"></div>
            <div v-for="h in visibleHours.slice(0,-1)" :key="'mvhh-'+h" class="v-line v-line-half" :style="{ left: timeToX(h,30)+'px' }"></div>
            <div v-if="nowVisible" class="now-vline" :style="{ left: nowX+'px' }"><div class="now-pip"></div></div>

            <div v-for="h in visibleHours.slice(0,-1)" :key="'mslot-'+h"
              class="mob-slot"
              :style="{ left: timeToX(h,0)+'px', width: pixelsPerHour+'px', top: 0, bottom: 0 }"
              @click="handleMobSlotTap(h)">
            </div>

            <div v-for="ev in activeTechEvents" :key="'mev-'+ev.id"
              class="ev-block mob-ev"
              :class="{
                'ev-pending': ev._pending,
                'popup-open': openPopupId === ev.id,
                'popup-below': ev._pending
              }"
              :style="mobEventStyle(ev)"
              @click.stop="ev._pending ? togglePendingPopup(ev) : $emit('event-click', ev)">
              <div class="ev-accent" :style="{ background: activeTech && activeTech.color||'#3b82f6' }"></div>
              <div class="ev-content">
                <div class="ev-title">{{ ev.title }}</div>
                <div class="ev-time">{{ formatTime(ev.startTime) }}–{{ formatTime(ev.endTime) }}</div>
                <div class="ev-client" v-if="ev.client">{{ ev.client }}</div>
              </div>
              <div v-if="ev._pending" class="ev-actions-popup" @click.stop>
                <button class="ev-btn ev-btn-cancel" @click="cancelPlacement(ev); openPopupId = null">
                  <svg width="10" height="10" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="3"><path d="M18 6L6 18M6 6l12 12"/></svg>
                </button>
                <button class="ev-btn ev-btn-confirm" @click="confirmPlacement(ev); openPopupId = null">
                  <svg width="10" height="10" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="3"><path d="M20 6L9 17l-5-5"/></svg>
                </button>
              </div>
              <div v-if="!ev._pending" class="ev-status" :class="'s-'+(ev.status||'planned')"></div>
            </div>
          </div>
        </div>
      </div>

      <div class="mob-all-techs-bar">
        <div class="mob-all-techs-inner">
          <div v-for="tech in technicians" :key="'mat-'+tech.id"
            class="mob-mini-tech"
            :class="{ active: activeTechId === tech.id }"
            :style="activeTechId === tech.id ? { borderColor: tech.color, background: tech.color+'12' } : {}"
            @click="activeTechId = tech.id">
            <div class="mob-mini-avatar" :style="{ background: tech.color||'#3b82f6' }">{{ initials(tech.name) }}</div>
            <div class="mob-mini-name">{{ tech.name.split(' ')[0] }}</div>
            <div class="mob-mini-count" :style="{ color: tech.color }">{{ techEventCount(tech.id) }}</div>
          </div>
        </div>
      </div>
    </div>

    <!-- DRAG GHOST -->
    <div v-if="dragState.active && !isMobile" class="drag-ghost" :style="ghostStyle">
      <span class="ghost-time">{{ dragState.preview ? dragState.preview.startLabel + ' → ' + dragState.preview.endLabel : '' }}</span>
      <span class="ghost-title">{{ dragState.item ? dragState.item.title : '' }}</span>
    </div>

  </div>
</template>

<script>
const AUTO_SCROLL_ZONE = 60
const AUTO_SCROLL_SPEED = 12
const MOBILE_BREAKPOINT = 768

export default {
  name: 'TechnicianPlannerHorizontalResponsive',

  props: {
    technicians:        { type: Array,   default: () => [] },
    startHour:          { type: Number,  default: 7 },
    endHour:            { type: Number,  default: 20 },
    events:             { type: Array,   default: () => [] },
    unscheduledEvents:  { type: Array,   default: () => [] },
    pixelsPerHour:      { type: Number,  default: 100 },
    rowHeight:          { type: Number,  default: 72 },
    showNowLine:        { type: Boolean, default: true },
    loadingUnscheduled: { type: Boolean, default: false },
    loadingTimeline:    { type: Boolean, default: false },
  },

  emits: ['event-click', 'event-scheduled', 'date-change'],

  data() {
    const today = new Date()
    const pad = n => String(n).padStart(2, '0')
    const todayStr = `${today.getFullYear()}-${pad(today.getMonth()+1)}-${pad(today.getDate())}`
    return {
      nowX: 0,
      nowInterval: null,
      pendingPlaced: [],
      isMobile: false,
      selectedDate: todayStr,
      pickerYear:   today.getFullYear(),
      pickerMonth:  today.getMonth(),
      pickerMode:   'days',
      activeTechId: null,
      showDateSheet: false,
      showUnscheduledSheet: false,
      showTechDropdown: false,
      openPopupId: null,
      mobileScheduleSheet: {
        show: false, event: null, techId: null,
        startH: today.getHours(), startM: 0,
      },
      dragState: {
        active: false, type: null, item: null, durationMins: 0,
        mouseX: 0, mouseY: 0, targetTechId: null, preview: null,
      },
      autoScrollRAF: null,
      panState: {
        active: false, startX: 0, startY: 0,
        startScrollLeft: 0, startScrollTop: 0,
      },
    }
  },

  computed: {
    mobRowHeight() { return 120 },
    visibleHours() {
      const h = []
      for (let i = this.startHour; i <= this.endHour; i++) h.push(i)
      return h
    },
    totalHours()      { return this.endHour - this.startHour },
    timelineWidth()   { return this.totalHours * this.pixelsPerHour + 80 },
    gridTotalHeight() { return this.technicians.length * this.rowHeight },
    pendingIds()      { return new Set(this.pendingPlaced.map(e => String(e.id))) },
    pendingCount()    { return this.pendingPlaced.length },
    confirmedIds()    { return new Set(this.events.map(e => String(e.id))) },
    sidebarEvents() {
      return this.unscheduledEvents.filter(
        e => !this.pendingIds.has(String(e.id)) && !this.confirmedIds.has(String(e.id))
      )
    },
    isToday() {
      const t = new Date()
      const pad = n => String(n).padStart(2, '0')
      return this.selectedDate === `${t.getFullYear()}-${pad(t.getMonth()+1)}-${pad(t.getDate())}`
    },
    nowVisible() {
      if (!this.showNowLine || !this.isToday) return false
      const now = new Date()
      const h = now.getHours() + now.getMinutes() / 60
      return h >= this.startHour && h <= this.endHour
    },
    pickerMonthName() {
      return new Date(this.pickerYear, this.pickerMonth, 1)
        .toLocaleDateString('fr-FR', { month: 'short', year: 'numeric' })
    },
    pickerDays() {
      const days = []
      const firstDay = new Date(this.pickerYear, this.pickerMonth, 1)
      let startDow = (firstDay.getDay() + 6) % 7
      for (let i = 0; i < startDow; i++) days.push(null)
      const daysInMonth = new Date(this.pickerYear, this.pickerMonth + 1, 0).getDate()
      for (let d = 1; d <= daysInMonth; d++) {
        const pad = n => String(n).padStart(2, '0')
        days.push({ day: d, date: `${this.pickerYear}-${pad(this.pickerMonth+1)}-${pad(d)}` })
      }
      return days
    },
    ghostStyle() {
      if (!this.dragState.active) return {}
      return { left: this.dragState.mouseX + 12 + 'px', top: this.dragState.mouseY - 18 + 'px' }
    },
    activeTech() {
      if (!this.activeTechId) return this.technicians[0] || null
      return this.technicians.find(t => t.id === this.activeTechId) || this.technicians[0] || null
    },
    activeTechEvents() {
      if (!this.activeTech) return []
      return this.techEvents(this.activeTech.id)
    },
    selectedDateLabel() {
      if (!this.selectedDate) return ''
      const d = new Date(this.selectedDate + 'T00:00:00')
      if (this.isMobile) {
        return d.toLocaleDateString('fr-FR', { weekday: 'short', day: 'numeric', month: 'short' })
      }
      return d.toLocaleDateString('fr-FR', { weekday: 'long', day: 'numeric', month: 'long', year: 'numeric' })
    },
    mobileSchedulePreviewLabel() {
      const { startH, startM, event } = this.mobileScheduleSheet
      if (!event) return ''
      const dur = event.duration || 60
      const endMins = startH * 60 + startM + dur
      const eh = Math.floor(endMins / 60), em = endMins % 60
      const fmt = (h, m) => `${String(h).padStart(2,'0')}:${String(m).padStart(2,'00')}`
      return `${fmt(startH, startM)} → ${fmt(eh, em)} (${this.formatDuration(dur)})`
    },
    dropdownStyle() {
      if (!this.showTechDropdown) return {}
      const wrap = this.$refs.techDropdownWrap
      if (!wrap) return {}
      const rect = wrap.getBoundingClientRect()
      return {
        position: 'fixed',
        top: (rect.bottom + 6) + 'px',
        left: rect.left + 'px',
        width: Math.max(rect.width, 200) + 'px',
      }
    },
  },

  watch: {
    events(newEvents) {
      const confirmed = new Set(newEvents.map(e => String(e.id)))
      this.pendingPlaced = this.pendingPlaced.filter(e => !confirmed.has(String(e.id)))
    },
    technicians: {
      immediate: true,
      handler(val) {
        if (val.length && !this.activeTechId) this.activeTechId = val[0].id
      }
    }
  },

  methods: {
    checkMobile() { this.isMobile = window.innerWidth < MOBILE_BREAKPOINT },
    initials(name) {
      return name.split(' ').map(p => p[0]).join('').toUpperCase().slice(0, 2)
    },
    toDateStr(date) {
      const pad = n => String(n).padStart(2, '0')
      return `${date.getFullYear()}-${pad(date.getMonth()+1)}-${pad(date.getDate())}`
    },
    isTodayDate(dateStr) { return dateStr === this.toDateStr(new Date()) },
    selectDate(dateStr) {
      if (!dateStr) return
      this.pendingPlaced = []
      this.selectedDate = dateStr
      this.$emit('date-change', dateStr)
    },
    offsetDay(delta) {
      const d = new Date(this.selectedDate + 'T00:00:00')
      d.setDate(d.getDate() + delta)
      this.pickerYear  = d.getFullYear()
      this.pickerMonth = d.getMonth()
      this.selectDate(this.toDateStr(d))
    },
    prevMonth() {
      if (this.pickerMonth === 0) { this.pickerMonth = 11; this.pickerYear-- }
      else this.pickerMonth--
    },
    nextMonth() {
      if (this.pickerMonth === 11) { this.pickerMonth = 0; this.pickerYear++ }
      else this.pickerMonth++
    },
    goToday() {
      const today = new Date()
      this.pickerYear  = today.getFullYear()
      this.pickerMonth = today.getMonth()
      this.selectDate(this.toDateStr(today))
    },
    timeToX(hour, min = 0) {
      return ((hour - this.startHour) * 60 + min) / 60 * this.pixelsPerHour
    },
    xToSnapped(x) {
      const totalMins = Math.round((x / this.pixelsPerHour * 60) / 10) * 10
      const bounded = Math.max(0, Math.min(this.totalHours * 60, totalMins))
      const h = Math.floor(bounded / 60) + this.startHour
      const m = bounded % 60
      return { h, m, label: `${String(h).padStart(2,'0')}:${String(m).padStart(2,'00')}` }
    },
    formatTime(t) { return t ? t.slice(0, 5) : '' },
    formatDuration(mins) {
      if (!mins) return ''
      const h = Math.floor(mins / 60), m = mins % 60
      return h ? (m ? `${h}h${String(m).padStart(2,'0')}` : `${h}h`) : `${m}min`
    },
    techEvents(id) {
      const confirmed = this.events.filter(e => e.technicianId === id)
      const pending   = this.pendingPlaced.filter(e => e.technicianId === id && !this.confirmedIds.has(String(e.id)))
      return [...confirmed, ...pending]
    },
    techEventCount(id) { return this.techEvents(id).length },
    eventDurationMins(ev) {
      const [sh, sm] = ev.startTime.split(':').map(Number)
      const [eh, em] = ev.endTime.split(':').map(Number)
      return (eh * 60 + em) - (sh * 60 + sm)
    },
    eventStyle(ev, techColor) {
      const [sh, sm] = ev.startTime.split(':').map(Number)
      const left = this.timeToX(sh, sm)
      const durMins = ev._pending && ev.duration ? ev.duration : this.eventDurationMins(ev)
      const width = Math.max(40, durMins / 60 * this.pixelsPerHour - 4)
      const c = ev.color || techColor || '#3b82f6'
      return { left: `${left}px`, width: `${width}px`, top: '6px', height: `${this.rowHeight - 12}px`, '--ec': c, background: c + '18', borderColor: c + '50' }
    },
    mobEventStyle(ev) {
      const [sh, sm] = ev.startTime.split(':').map(Number)
      const left = this.timeToX(sh, sm)
      const durMins = ev._pending && ev.duration ? ev.duration : this.eventDurationMins(ev)
      const width = Math.max(40, durMins / 60 * this.pixelsPerHour - 4)
      const c = ev.color || (this.activeTech && this.activeTech.color) || '#3b82f6'
      return { left: `${left}px`, width: `${width}px`, top: '6px', height: `${this.mobRowHeight - 12}px`, '--ec': c, background: c + '18', borderColor: c + '50' }
    },
    updateNow() {
      const n = new Date()
      const clampedH = Math.max(this.startHour, Math.min(this.endHour, n.getHours() + n.getMinutes() / 60))
      this.nowX = this.timeToX(Math.floor(clampedH), Math.round((clampedH % 1) * 60))
    },
    onGridScroll() {
      const gs = this.$refs.gridScroll
      if (!gs) return
      if (this.$refs.timeHead)       this.$refs.timeHead.scrollLeft      = gs.scrollLeft
      if (this.$refs.resourceLabels) this.$refs.resourceLabels.scrollTop = gs.scrollTop
    },
    onMobGridScroll() {
      const gs = this.$refs.mobGridScroll
      if (!gs) return
      if (this.$refs.mobTimeHead) this.$refs.mobTimeHead.scrollLeft = gs.scrollLeft
    },
    onGridMouseDown(e) {
      if (e.button !== 0 || this.dragState.active) return
      if (e.target.closest('.ev-block, .ev-btn, .ev-actions-popup')) return
      e.preventDefault()
      const gs = this.$refs.gridScroll
      this.panState = { active: true, startX: e.clientX, startY: e.clientY, startScrollLeft: gs.scrollLeft, startScrollTop: gs.scrollTop }
      document.addEventListener('mousemove', this._onPanMove, { passive: false })
      document.addEventListener('mouseup',   this._onPanUp)
    },
    _onPanMove(e) {
      if (!this.panState.active) return
      e.preventDefault()
      const gs = this.$refs.gridScroll
      if (!gs) return
      gs.scrollLeft = this.panState.startScrollLeft - (e.clientX - this.panState.startX)
      gs.scrollTop  = this.panState.startScrollTop  - (e.clientY - this.panState.startY)
      if (this.$refs.timeHead)       this.$refs.timeHead.scrollLeft      = gs.scrollLeft
      if (this.$refs.resourceLabels) this.$refs.resourceLabels.scrollTop = gs.scrollTop
    },
    _onPanUp() {
      this.panState.active = false
      document.removeEventListener('mousemove', this._onPanMove)
      document.removeEventListener('mouseup',   this._onPanUp)
    },
    startDrag(e, ev, type) {
      if (e.button !== 0) return
      e.preventDefault()
      const durationMins = type === 'pending' ? (Number(ev.duration) || this.eventDurationMins(ev)) : (Number(ev.duration) || 60)
      this.dragState = { active: true, type, item: ev, durationMins, mouseX: e.clientX, mouseY: e.clientY, targetTechId: null, preview: null }
      document.addEventListener('mousemove', this._onMouseMove, { passive: false })
      document.addEventListener('mouseup',   this._onMouseUp)
      this.startAutoScroll()
    },
    _onMouseMove(e) {
      e.preventDefault()
      this.dragState.mouseX = e.clientX
      this.dragState.mouseY = e.clientY
      this.updateDragPreview(e.clientX, e.clientY)
    },
    _onMouseUp() {
      document.removeEventListener('mousemove', this._onMouseMove)
      document.removeEventListener('mouseup',   this._onMouseUp)
      this.stopAutoScroll()
      if (this.dragState.preview && this.dragState.targetTechId) this.commitDrop()
      else this.cancelDrag()
    },
    updateDragPreview(clientX, clientY) {
      const gs = this.$refs.gridScroll
      if (!gs) return
      const rect = gs.getBoundingClientRect()
      if (clientX < rect.left || clientX > rect.right || clientY < rect.top || clientY > rect.bottom) {
        this.dragState.targetTechId = null; this.dragState.preview = null; return
      }
      const xInGrid = clientX - rect.left + gs.scrollLeft
      const rawSnap = this.xToSnapped(xInGrid)
      const duration = Number(this.dragState.durationMins)
      const rawStartMins = (Number(rawSnap.h) - this.startHour) * 60 + Number(rawSnap.m)
      const maxStartMins = Number(this.totalHours) * 60 - duration
      const clampedStartMins = Math.max(0, Math.min(rawStartMins, maxStartMins))
      const startAbsMins = this.startHour * 60 + clampedStartMins
      const startH = Math.floor(startAbsMins / 60), startM = startAbsMins % 60
      const startLabel = `${String(startH).padStart(2,'0')}:${String(startM).padStart(2,'00')}`
      const startX = this.timeToX(startH, startM)
      const endAbsMins = startAbsMins + duration
      const endH = Math.floor(endAbsMins / 60), endM = endAbsMins % 60
      const endLabel = `${String(endH).padStart(2,'0')}:${String(endM).padStart(2,'00')}`
      const width = Math.max(40, duration / 60 * this.pixelsPerHour)
      const yInGrid = clientY - rect.top + gs.scrollTop
      const rowIdx = Math.floor(yInGrid / this.rowHeight)
      const tech = this.technicians[Math.max(0, Math.min(rowIdx, this.technicians.length - 1))]
      this.dragState.targetTechId = tech?.id || null
      this.dragState.preview = { left: startX, width, startLabel, endLabel, startTime: startLabel, endTime: endLabel }
    },
    commitDrop() {
      const { item, targetTechId, preview } = this.dragState
      const tech = this.technicians.find(t => t.id === targetTechId)
      const dur  = this.dragState.durationMins
      const [sh, sm] = preview.startTime.split(':').map(Number)
      const endAbsMins = sh * 60 + sm + dur
      const endH = Math.floor(endAbsMins / 60), endM = endAbsMins % 60
      const endTime = `${String(endH).padStart(2,'0')}:${String(endM).padStart(2,'00')}`
      const rest = this.pendingPlaced.filter(e => String(e.id) !== String(item.id))
      this.pendingPlaced = [...rest, { ...item, _pending: true, technicianId: targetTechId, startTime: preview.startTime, endTime, status: 'planned', color: tech?.color || null, duration: dur }]
      this.dragState = { active: false, type: null, item: null, durationMins: 0, mouseX: 0, mouseY: 0, targetTechId: null, preview: null }
    },
    cancelDrag() {
      this.dragState = { active: false, type: null, item: null, durationMins: 0, mouseX: 0, mouseY: 0, targetTechId: null, preview: null }
    },
    startAutoScroll() {
      const tick = () => {
        if (!this.dragState.active) return
        const gs = this.$refs.gridScroll
        if (!gs) { this.autoScrollRAF = requestAnimationFrame(tick); return }
        const rect = gs.getBoundingClientRect()
        const mx = this.dragState.mouseX, my = this.dragState.mouseY
        let scrolled = false
        if (mx < rect.left + AUTO_SCROLL_ZONE) { gs.scrollLeft -= AUTO_SCROLL_SPEED; scrolled = true }
        else if (mx > rect.right - AUTO_SCROLL_ZONE) { gs.scrollLeft += AUTO_SCROLL_SPEED; scrolled = true }
        if (my < rect.top + AUTO_SCROLL_ZONE) { gs.scrollTop -= AUTO_SCROLL_SPEED; scrolled = true }
        else if (my > rect.bottom - AUTO_SCROLL_ZONE) { gs.scrollTop += AUTO_SCROLL_SPEED; scrolled = true }
        if (scrolled) {
          if (this.$refs.timeHead) this.$refs.timeHead.scrollLeft = gs.scrollLeft
          if (this.$refs.resourceLabels) this.$refs.resourceLabels.scrollTop = gs.scrollTop
          this.updateDragPreview(this.dragState.mouseX, this.dragState.mouseY)
        }
        this.autoScrollRAF = requestAnimationFrame(tick)
      }
      this.autoScrollRAF = requestAnimationFrame(tick)
    },
    stopAutoScroll() {
      if (this.autoScrollRAF) { cancelAnimationFrame(this.autoScrollRAF); this.autoScrollRAF = null }
    },
    confirmPlacement(ev) {
      this.$emit('event-scheduled', { event: ev, technicianId: ev.technicianId, startTime: ev.startTime, endTime: ev.endTime })
      this.pendingPlaced = this.pendingPlaced.filter(e => String(e.id) !== String(ev.id))
    },
    cancelPlacement(ev) {
      this.pendingPlaced = this.pendingPlaced.filter(e => String(e.id) !== String(ev.id))
    },
    togglePendingPopup(ev) {
      this.openPopupId = this.openPopupId === ev.id ? null : ev.id
    },
    mobileScheduleEvent(ev) {
      this.mobileScheduleSheet = {
        show: true, event: ev,
        techId: this.activeTechId || (this.technicians[0] && this.technicians[0].id),
        startH: this.startHour + 1, startM: 0,
      }
      this.showUnscheduledSheet = false
    },
    handleMobSlotTap(h) {
      if (this.sidebarEvents.length > 0) {
        this.mobileScheduleSheet = {
          show: true, event: this.sidebarEvents[0],
          techId: this.activeTechId,
          startH: h, startM: 0,
        }
      }
    },
    confirmMobileSchedule() {
      const { event, techId, startH, startM } = this.mobileScheduleSheet
      if (!techId || !event) return
      const tech = this.technicians.find(t => t.id === techId)
      const dur = event.duration || 60
      const startTime = `${String(startH).padStart(2,'0')}:${String(startM).padStart(2,'00')}`
      const endMins = startH * 60 + startM + dur
      const endH = Math.floor(endMins / 60), endM = endMins % 60
      const endTime = `${String(endH).padStart(2,'0')}:${String(endM).padStart(2,'00')}`
      const rest = this.pendingPlaced.filter(e => String(e.id) !== String(event.id))
      this.pendingPlaced = [...rest, { ...event, _pending: true, technicianId: techId, startTime, endTime, status: 'planned', color: tech?.color || null, duration: dur }]
      this.activeTechId = techId
      this.mobileScheduleSheet.show = false
    },
    toggleTechDropdown() {
      this.showTechDropdown = !this.showTechDropdown
    },
  },

  mounted() {
    this.checkMobile()
    this._resizeListener = () => this.checkMobile()
    window.addEventListener('resize', this._resizeListener)
    this._dropdownClose = (e) => {
      if (this.showTechDropdown && !e.target.closest('.tb-tech-dropdown-wrap') && !e.target.closest('.tb-tech-dropdown-list')) {
        this.showTechDropdown = false
      }
      if (this.openPopupId && !e.target.closest('.ev-block.ev-pending')) {
        this.openPopupId = null
      }
    }
    document.addEventListener('click', this._dropdownClose)
    this.updateNow()
    this.nowInterval = setInterval(this.updateNow, 60000)
    this._onMouseMove = this._onMouseMove.bind(this)
    this._onMouseUp   = this._onMouseUp.bind(this)
    this._onPanMove   = this._onPanMove.bind(this)
    this._onPanUp     = this._onPanUp.bind(this)
    this.$nextTick(() => {
      if (this.$refs.gridScroll && this.nowVisible) {
        this.$refs.gridScroll.scrollLeft = Math.max(0, this.nowX - 160)
      }
      if (this.$refs.mobGridScroll && this.nowVisible) {
        this.$refs.mobGridScroll.scrollLeft = Math.max(0, this.nowX - 80)
      }
    })
  },

  beforeUnmount() {
    clearInterval(this.nowInterval)
    this.stopAutoScroll()
    window.removeEventListener('resize', this._resizeListener)
    document.removeEventListener('click', this._dropdownClose)
    document.removeEventListener('mousemove', this._onMouseMove)
    document.removeEventListener('mouseup',   this._onMouseUp)
    document.removeEventListener('mousemove', this._onPanMove)
    document.removeEventListener('mouseup',   this._onPanUp)
  }
}
</script>

<style scoped>
@import url('https://fonts.googleapis.com/css2?family=Figtree:wght@300;400;500;600;700&family=JetBrains+Mono:wght@400;500&display=swap');

*, *::before, *::after { box-sizing: border-box; margin: 0; padding: 0; }

.planner-root {
  font-family: 'Figtree', sans-serif;
  display: flex; flex-direction: column;
  height: 100dvh; min-height: 400px;
  background: #f0f2f5; color: #1a1d27;
  overflow: hidden; user-select: none;
}

/* ══ TOP BAR ══ */
.top-bar {
  display: flex; align-items: center; gap: 8px;
  padding: 0 12px; height: 52px;
  background: #fff; border-bottom: 1px solid #e2e6ed;
  flex-shrink: 0; z-index: 20; position: relative; overflow: visible;
  box-shadow: 0 1px 8px #00000008;
}
.tb-date-btn {
  display: flex; align-items: center; gap: 6px;
  background: #f8f9fb; border: 1px solid #e2e6ed; border-radius: 10px;
  padding: 7px 11px; cursor: pointer;
  font-family: 'Figtree', sans-serif; font-size: 13px; font-weight: 600; color: #1a1d27;
  touch-action: manipulation; transition: all 0.12s; white-space: nowrap;
}
.tb-date-btn:active { background: #eff4ff; border-color: #3b82f6; }
.tb-date-static {
  display: flex; align-items: center; gap: 7px;
  font-size: 13.5px; font-weight: 600; color: #1a1d27; text-transform: capitalize;
}
.tb-arrows { display: flex; gap: 3px; }
.tb-arrow-btn {
  width: 34px; height: 34px; border-radius: 8px;
  border: 1px solid #e2e6ed; background: #f8f9fb;
  display: flex; align-items: center; justify-content: center;
  cursor: pointer; color: #64748b; touch-action: manipulation; transition: all 0.12s;
}
.tb-arrow-btn:active { background: #eff4ff; border-color: #3b82f6; }
.tb-right { display: flex; align-items: center; gap: 8px; margin-left: auto; overflow: visible; position: relative; }
.tb-tech-dropdown-wrap { position: relative; flex: 1; min-width: 0; max-width: 200px; }
.tb-tech-dropdown-btn {
  display: flex; align-items: center; gap: 6px; width: 100%;
  padding: 7px 10px; border-radius: 10px;
  border: 1.5px solid #e2e6ed; background: #fff;
  font-family: 'Figtree', sans-serif; font-size: 12.5px; font-weight: 600; color: #64748b;
  cursor: pointer; touch-action: manipulation; transition: all 0.15s;
  white-space: nowrap; overflow: hidden;
}
.tb-tech-dropdown-btn:active { background: #f8f9fb; }
.tb-tech-dot { width: 8px; height: 8px; border-radius: 50%; flex-shrink: 0; }
.tb-tech-dropdown-name { flex: 1; overflow: hidden; text-overflow: ellipsis; text-align: left; }
.tb-tech-dropdown-list {
  position: fixed;
  background: #fff; border: 1px solid #e2e6ed; border-radius: 12px;
  box-shadow: 0 8px 24px rgba(0,0,0,0.15); z-index: 9000;
  overflow: hidden; padding: 4px; min-width: 200px;
}
.tb-tech-dropdown-item {
  display: flex; align-items: center; gap: 9px; width: 100%;
  padding: 9px 10px; border-radius: 8px; border: none; background: transparent;
  font-family: 'Figtree', sans-serif; font-size: 13px; color: #374151;
  cursor: pointer; touch-action: manipulation; transition: background 0.1s; text-align: left;
}
.tb-tech-dropdown-item:active, .tb-tech-dropdown-item:hover { background: #f8f9fb; }
.tb-tech-dropdown-item.active { background: #eff6ff; font-weight: 600; }
.tb-tech-dropdown-avatar { width: 26px; height: 26px; border-radius: 7px; color: #fff; font-size: 9.5px; font-weight: 700; display: flex; align-items: center; justify-content: center; flex-shrink: 0; }
.tb-tech-dropdown-info { display: flex; flex-direction: column; flex: 1; min-width: 0; }
.tb-tech-dropdown-label { font-size: 13px; font-weight: 600; white-space: nowrap; overflow: hidden; text-overflow: ellipsis; }
.tb-tech-dropdown-role { font-size: 10.5px; color: #9ba3b2; }
.tb-unscheduled-btn {
  position: relative; width: 38px; height: 38px; border-radius: 10px;
  border: 1.5px solid #e2e6ed; background: #fff; color: #64748b;
  display: flex; align-items: center; justify-content: center;
  cursor: pointer; touch-action: manipulation; flex-shrink: 0; transition: all 0.12s;
}
.tb-unscheduled-btn.has-events { border-color: #3b82f6; color: #3b82f6; background: #eff6ff; }
.tb-badge {
  position: absolute; top: -5px; right: -5px;
  background: #ef4444; color: #fff; font-size: 10px; font-weight: 700;
  min-width: 17px; height: 17px; border-radius: 9px;
  display: flex; align-items: center; justify-content: center; padding: 0 4px;
  border: 2px solid #fff;
}
.tb-today-btn {
  padding: 7px 14px; border-radius: 8px; border: 1px solid #e2e6ed;
  background: #fff; color: #64748b; cursor: pointer;
  font-family: 'Figtree', sans-serif; font-size: 12.5px; font-weight: 500;
  transition: all 0.12s; white-space: nowrap;
}
.tb-today-btn:hover { background: #f1f5f9; border-color: #3b82f6; color: #3b82f6; }
.tb-today-btn.active { border-color: #3b82f6; color: #3b82f6; background: #eff6ff; }

/* ══ DESKTOP LAYOUT ══ */
.desktop-layout { flex: 1; display: flex; overflow: hidden; }
.sidebar {
  width: 232px; flex-shrink: 0; background: #fff;
  border-right: 1px solid #e2e6ed; display: flex; flex-direction: column;
  overflow: hidden; box-shadow: 2px 0 12px #00000008; z-index: 10;
}
.sidebar-header {
  padding: 16px 16px 12px; display: flex; align-items: center; gap: 8px;
  border-bottom: 1px solid #edf0f4; flex-shrink: 0;
}
.sidebar-label { font-size: 11px; font-weight: 700; text-transform: uppercase; letter-spacing: 1.3px; color: #9ba3b2; flex: 1; }
.sidebar-badge {
  background: #3b82f6; color: #fff; font-size: 11px; font-weight: 700;
  min-width: 22px; height: 22px; border-radius: 11px;
  display: flex; align-items: center; justify-content: center; padding: 0 6px;
}
.unscheduled-area { flex: 1; display: flex; flex-direction: column; overflow: hidden; position: relative; }
.unscheduled-list { flex: 1; overflow-y: auto; padding: 10px; display: flex; flex-direction: column; gap: 7px; }
.unscheduled-list::-webkit-scrollbar { width: 3px; }
.unscheduled-list::-webkit-scrollbar-thumb { background: #dde0e8; border-radius: 3px; }

.ucard {
  background: #fff; border: 1px solid #e2e6ed; border-radius: 10px;
  display: flex; align-items: stretch; cursor: grab; overflow: hidden;
  transition: box-shadow 0.14s, transform 0.14s, border-color 0.14s;
}
.ucard:hover { border-color: #3b82f6; box-shadow: 0 3px 16px #3b82f61a; transform: translateY(-1px); }
.ucard:active { cursor: grabbing; transform: scale(0.98); }
.ucard-stripe { width: 4px; flex-shrink: 0; }
.prio-urgent .ucard-stripe { background: #ef4444; }
.prio-high   .ucard-stripe { background: #f97316; }
.prio-normal .ucard-stripe { background: #3b82f6; }
.prio-low    .ucard-stripe { background: #c8d0dd; }
.ucard-body { flex: 1; padding: 9px 8px; min-width: 0; }
.ucard-title { font-size: 12.5px; font-weight: 600; color: #1a1d27; white-space: nowrap; overflow: hidden; text-overflow: ellipsis; margin-bottom: 3px; }
.ucard-row { font-size: 11px; color: #9ba3b2; display: flex; align-items: center; gap: 4px; margin-top: 2px; white-space: nowrap; overflow: hidden; text-overflow: ellipsis; }
.ucard-tags { display: flex; flex-wrap: wrap; gap: 3px; margin-top: 5px; }
.utag { font-size: 10px; background: #eff4ff; color: #3b82f6; border: 1px solid #dbeafe; padding: 1px 6px; border-radius: 4px; font-weight: 500; }
.drag-grip { color: #d0d5df; flex-shrink: 0; align-self: center; margin: 0 7px 0 2px; }
.ucard-action { display: flex; align-items: center; justify-content: center; padding: 0 10px; color: #3b82f6; }
.empty-state { display: flex; flex-direction: column; align-items: center; gap: 10px; color: #c4c9d4; font-size: 12px; padding: 36px 8px; text-align: center; }

.timeline-wrap {
  flex: 1; display: grid;
  grid-template-columns: 160px 1fr;
  grid-template-rows: 36px 1fr;
  overflow: hidden; position: relative;
}
.tl-corner {
  grid-column: 1; grid-row: 1; background: #fff;
  border-right: 1px solid #e2e6ed; border-bottom: 1px solid #e2e6ed;
  display: flex; align-items: center; padding: 0 14px; z-index: 5;
  box-shadow: 2px 2px 8px #0000000a;
}
.corner-label { font-size: 10px; font-weight: 700; text-transform: uppercase; letter-spacing: 1.2px; color: #b0b7c3; }
.tl-time-head {
  grid-column: 2; grid-row: 1; background: #fff;
  border-bottom: 1px solid #e2e6ed; overflow: hidden;
  position: relative; box-shadow: 0 2px 8px #0000000a; z-index: 4;
}
.tl-time-inner { position: relative; height: 100%; overflow: hidden; }
.time-tick { position: absolute; top: 0; bottom: 0; display: flex; align-items: center; transform: translateX(-50%); }
.time-tick-label { font-size: 10px; font-family: 'JetBrains Mono', monospace; color: #94a3b8; font-weight: 500; white-space: nowrap; }
.time-tick-label.half { color: #c4cdd6; font-size: 9px; }
.tl-resource-labels {
  grid-column: 1; grid-row: 2; background: #fff;
  border-right: 1px solid #e2e6ed; overflow: hidden; z-index: 3; box-shadow: 2px 0 8px #0000000a;
}
.resource-label {
  display: flex; align-items: center; gap: 10px; padding: 0 12px 0 16px;
  border-bottom: 1px solid #edf0f4; border-left: 3px solid transparent;
  background: #fff; transition: background 0.1s;
}
.resource-label.row-active { background: #f0f4ff; }
.tech-avatar { width: 32px; height: 32px; border-radius: 8px; color: #fff; font-size: 11px; font-weight: 700; display: flex; align-items: center; justify-content: center; flex-shrink: 0; }
.tech-meta { flex: 1; min-width: 0; }
.tech-name { font-size: 12.5px; font-weight: 600; color: #1a1d27; white-space: nowrap; overflow: hidden; text-overflow: ellipsis; }
.tech-role { font-size: 10.5px; color: #9ba3b2; }
.tech-count { font-size: 12px; font-weight: 700; font-family: 'JetBrains Mono', monospace; flex-shrink: 0; }
.tl-grid-scroll {
  grid-column: 2; grid-row: 2; overflow: auto; position: relative;
  background: #f0f2f5; cursor: grab;
}
.tl-grid-scroll.is-panning { cursor: grabbing; }
.tl-grid-scroll.is-dragging { cursor: default; }
.tl-grid-scroll::-webkit-scrollbar { width: 6px; height: 6px; }
.tl-grid-scroll::-webkit-scrollbar-track { background: #f0f2f5; }
.tl-grid-scroll::-webkit-scrollbar-thumb { background: #d0d5df; border-radius: 6px; }
.tl-grid-inner { position: relative; background: #fff; }

.v-line { position: absolute; top: 0; bottom: 0; width: 1px; background: #edf0f4; pointer-events: none; }
.v-line-half { background: #f5f6f8; }
.h-sep { position: absolute; left: 0; right: 0; height: 1px; background: #edf0f4; pointer-events: none; }
.now-vline { position: absolute; top: 0; bottom: 0; width: 2px; background: #ef4444; z-index: 5; pointer-events: none; }
.now-pip { width: 8px; height: 8px; background: #ef4444; border-radius: 50%; position: absolute; top: 0; left: -3px; box-shadow: 0 0 0 3px #ef444428; }
.tech-row { position: absolute; left: 0; right: 0; }
.tech-row.row-drop-active { background: #f0f4ff; }
.drop-preview {
  position: absolute; top: 5px; bottom: 5px;
  border: 2px dashed var(--pc, #3b82f6); border-radius: 8px;
  background: color-mix(in srgb, var(--pc, #3b82f6) 8%, transparent);
  pointer-events: none; z-index: 6;
  display: flex; align-items: center; padding: 0 8px;
  animation: preview-pulse 0.6s ease-in-out infinite alternate;
}
@keyframes preview-pulse { from { opacity: 0.5; } to { opacity: 1; } }
.preview-label { font-size: 10px; font-family: 'JetBrains Mono', monospace; color: var(--pc, #3b82f6); font-weight: 600; white-space: nowrap; }

/* ══════════════════════════════════════════
   EVENT BLOCKS
══════════════════════════════════════════ */
.ev-block {
  position: absolute; border: 1px solid; border-radius: 8px;
  display: flex; align-items: stretch; overflow: hidden;
  cursor: pointer; z-index: 2; transition: box-shadow 0.12s, opacity 0.12s;
}
.ev-block:hover { box-shadow: 0 3px 16px color-mix(in srgb, var(--ec, #3b82f6) 20%, transparent); z-index: 4; }
.ev-block:active { transform: scale(0.98); }

/* Blocs pending : overflow visible pour que le popover sorte */
.ev-block.ev-pending {
  border-style: dashed; border-width: 1.5px; cursor: grab;
  background: color-mix(in srgb, var(--ec, #3b82f6) 7%, #fff) !important;
  overflow: visible;
}
.ev-block.ev-dragging { opacity: 0.3; }
.ev-block.ev-pending .ev-accent,
.ev-block.ev-pending .ev-content {
  animation: pending-block 0.8s ease-in-out infinite;
}

.ev-accent { width: 4px; flex-shrink: 0; }
.ev-content { flex: 1; padding: 4px 6px; overflow: hidden; min-width: 0; display: flex; flex-direction: column; justify-content: center; }
.ev-title { font-size: 11.5px; font-weight: 600; color: #1a1d27; white-space: nowrap; overflow: hidden; text-overflow: ellipsis; line-height: 1.3; }
.ev-time { font-size: 9.5px; font-family: 'JetBrains Mono', monospace; color: #9ba3b2; margin-top: 1px; white-space: nowrap; }
.ev-client { font-size: 10px; color: #6b7280; white-space: nowrap; overflow: hidden; text-overflow: ellipsis; margin-top: 1px; }

/* ══════════════════════════════════════════
   POPUP FLOTTANTE — POSITIONNEMENT INTELLIGENT
   
   Deux problèmes résolus :
   1. Popup trop haute sur la 1ère ligne → classe popup-below force
      la popup en dessous (top: 100%) au lieu d'au-dessus (bottom: 100%)
      • Desktop : rowIdx === 0 → popup-below
      • Mobile  : toujours popup-below (une seule ligne visible)
   
   2. Popup hors zone visible sur bloc long (8h) → ancrage left: 8px
      au lieu de left: 50% + transform. La popup reste collée au
      début du bloc visible dès que tu scrolles dessus.
══════════════════════════════════════════ */
.ev-actions-popup {
  position: absolute;
  /* Ancré au début du bloc (pas au centre) */
  left: 8px;
  transform: none;
  /* Direction par défaut : AU-DESSUS */
  bottom: calc(100% + 6px);
  top: auto;
  z-index: 100;
  display: flex; flex-direction: row; gap: 4px;
  background: #fff; border: 1px solid #e2e6ed; border-radius: 9px; padding: 5px;
  box-shadow: 0 4px 20px rgba(0,0,0,0.14), 0 1px 4px rgba(0,0,0,0.08);
  white-space: nowrap; pointer-events: all;
  opacity: 0; visibility: hidden;
  transition: opacity 0.14s, visibility 0.14s;
}

/* Flèche pointant vers le bas (popup au-dessus) */
.ev-actions-popup::after {
  content: '';
  position: absolute; top: 100%; left: 14px;
  border: 5px solid transparent; border-top-color: #e2e6ed;
}
.ev-actions-popup::before {
  content: '';
  position: absolute; top: calc(100% - 1px); left: 14px; z-index: 1;
  border: 5px solid transparent; border-top-color: #fff;
}

/* ── Variante "en dessous" ──
   Appliquée quand rowIdx === 0 (desktop) ou toujours sur mobile
   via la classe .popup-below sur le .ev-block parent          */
.ev-block.ev-pending.popup-below .ev-actions-popup {
  bottom: auto;
  top: calc(100% + 6px);
}
/* Flèche pointant vers le haut (popup en dessous) */
.ev-block.ev-pending.popup-below .ev-actions-popup::after {
  top: auto; bottom: 100%;
  border-top-color: transparent; border-bottom-color: #e2e6ed;
}
.ev-block.ev-pending.popup-below .ev-actions-popup::before {
  top: auto; bottom: calc(100% - 1px);
  border-top-color: transparent; border-bottom-color: #fff;
}

/* Affichage */
.ev-block.ev-pending .ev-actions-popup {
  opacity: 1; visibility: visible;
}
/* .ev-block.ev-pending.popup-open .ev-actions-popup {
  opacity: 1; visibility: visible;
} */

.ev-btn {
  width: 26px; height: 26px; border-radius: 6px; border: none;
  display: flex; align-items: center; justify-content: center;
  cursor: pointer; touch-action: manipulation; transition: background 0.12s, transform 0.1s; flex-shrink: 0;
}
.ev-btn:active { transform: scale(1.1); }
.ev-btn-cancel  { background: #fee2e2; color: #ef4444; }
.ev-btn-cancel:hover  { background: #fecaca; }
.ev-btn-confirm { background: #dcfce7; color: #16a34a; }
.ev-btn-confirm:hover { background: #bbf7d0; }
.ev-btn-confirm {
  padding: 0 10px;
  gap: 5px;
  width: auto; /* override le width: 26px carré */
}
.ev-btn-confirm span { display: inline-block; font-size: 13px; font-weight: bold; }
.ev-btn-confirm svg { display: inline-block; vertical-align: middle; width: 16px; height: 16px; }

.ev-status { width: 6px; height: 6px; border-radius: 50%; flex-shrink: 0; align-self: center; margin: 0 7px 0 4px; }
.s-planned     { background: #3b82f6; }
.s-in-progress { background: #f59e0b; box-shadow: 0 0 0 2px #fef3c7; }
.s-done        { background: #22c55e; }
.s-cancelled   { background: #ef4444; }

/* ══ MOBILE LAYOUT ══ */
.mobile-layout { flex: 1; display: flex; flex-direction: column; overflow: hidden; }
.mob-tech-bar {
  display: flex; align-items: center; gap: 10px;
  padding: 10px 14px; background: #fff;
  border-bottom: 2px solid #e2e6ed; flex-shrink: 0;
}
.mob-tech-avatar { width: 34px; height: 34px; border-radius: 9px; color: #fff; font-size: 11px; font-weight: 700; display: flex; align-items: center; justify-content: center; flex-shrink: 0; }
.mob-tech-name { font-size: 13.5px; font-weight: 700; color: #1a1d27; }
.mob-tech-role { font-size: 11px; color: #9ba3b2; }
.mob-tech-count { margin-left: auto; font-size: 11px; font-weight: 700; font-family: 'JetBrains Mono', monospace; }
.mob-timeline-wrap { flex: 1; display: flex; flex-direction: column; overflow: hidden; position: relative; }
.mob-time-header {
  height: 32px; flex-shrink: 0; background: #fff;
  border-bottom: 1px solid #e2e6ed; overflow: hidden;
  box-shadow: 0 2px 8px #0000000a; z-index: 3;
}
.mob-time-inner { position: relative; height: 100%; }
.mob-time-tick {
  position: absolute; top: 0; bottom: 0;
  display: flex; align-items: center; transform: translateX(-50%);
  font-size: 9.5px; font-family: 'JetBrains Mono', monospace; color: #94a3b8; font-weight: 500; white-space: nowrap;
}
.mob-time-tick.half { color: #c4cdd6; font-size: 8.5px; }
.mob-grid-scroll {
  flex: 1; overflow-x: auto; overflow-y: hidden;
  -webkit-overflow-scrolling: touch; position: relative;
}
.mob-grid-scroll::-webkit-scrollbar { height: 4px; }
.mob-grid-scroll::-webkit-scrollbar-thumb { background: #d0d5df; border-radius: 4px; }
.mob-grid-inner { position: relative; background: #fff; }
.mob-slot {
  position: absolute; cursor: pointer; z-index: 1;
  background: transparent; transition: background 0.1s;
}
.mob-slot:active { background: #f0f4ff; }
.mob-ev { z-index: 2; }
.mob-all-techs-bar {
  flex-shrink: 0; background: #fff;
  border-top: 1px solid #e2e6ed; padding: 8px 0;
  box-shadow: 0 -2px 8px #00000008;
}
.mob-all-techs-inner {
  display: flex; gap: 6px; padding: 0 10px;
  overflow-x: auto; -webkit-overflow-scrolling: touch; scrollbar-width: none;
}
.mob-all-techs-inner::-webkit-scrollbar { display: none; }
.mob-mini-tech {
  display: flex; flex-direction: column; align-items: center; gap: 3px;
  padding: 6px 10px; border-radius: 10px;
  border: 1.5px solid #e2e6ed; background: #fff;
  cursor: pointer; touch-action: manipulation; flex-shrink: 0;
  transition: all 0.15s; min-width: 56px;
}
.mob-mini-tech:active { transform: scale(0.96); }
.mob-mini-avatar { width: 28px; height: 28px; border-radius: 7px; color: #fff; font-size: 9.5px; font-weight: 700; display: flex; align-items: center; justify-content: center; }
.mob-mini-name { font-size: 10.5px; font-weight: 600; color: #374151; white-space: nowrap; }
.mob-mini-count { font-size: 10px; font-family: 'JetBrains Mono', monospace; font-weight: 700; }

/* ══ LOADERS ══ */
.list-loader { flex: 1; display: flex; flex-direction: column; align-items: center; justify-content: center; gap: 10px; color: #b0b7c3; font-size: 12px; }
.timeline-loader { position: absolute; inset: 0; z-index: 20; background: rgba(255,255,255,0.82); backdrop-filter: blur(3px); display: flex; flex-direction: column; align-items: center; justify-content: center; gap: 12px; pointer-events: all; }
.loader-label { font-size: 12.5px; font-weight: 500; color: #94a3b8; }
.loader-spinner { width: 22px; height: 22px; border: 2.5px solid #e2e8f0; border-top-color: #3b82f6; border-radius: 50%; animation: spin 0.7s linear infinite; }
.loader-spinner.large { width: 32px; height: 32px; border-width: 3px; }
@keyframes spin { to { transform: rotate(360deg); } }

/* ══ DATE PICKER ══ */
.datepicker { padding: 8px 10px 8px; border-bottom: 1px solid #edf0f4; flex-shrink: 0; background: #fff; }
.dp-nav { display: flex; align-items: center; justify-content: space-between; margin-bottom: 6px; gap: 4px; }
.dp-nav-btn { width: 26px; height: 26px; border-radius: 6px; border: 1px solid #e2e6ed; background: #fff; color: #64748b; display: flex; align-items: center; justify-content: center; cursor: pointer; transition: all 0.12s; touch-action: manipulation; }
.dp-nav-btn:hover { background: #f1f5f9; border-color: #cbd5e1; color: #1a1d27; }
.dp-month-label { flex: 1; display: flex; align-items: center; justify-content: center; font-size: 11.5px; font-weight: 600; color: #1a1d27; text-transform: capitalize; background: none; border: none; cursor: pointer; padding: 3px 6px; border-radius: 6px; font-family: 'Figtree', sans-serif; transition: background 0.1s; white-space: nowrap; touch-action: manipulation; }
.dp-month-label:hover { background: #f1f5f9; }
.dp-weekdays { display: grid; grid-template-columns: repeat(7, 1fr); margin-bottom: 2px; }
.dp-wd { text-align: center; font-size: 9px; font-weight: 600; color: #c4cdd6; padding: 1px 0; }
.dp-grid { display: grid; grid-template-columns: repeat(7, 1fr); gap: 1px; }
.dp-cell { aspect-ratio: 1; border: none; background: transparent; border-radius: 5px; font-size: 10.5px; font-weight: 500; color: #374151; cursor: pointer; display: flex; align-items: center; justify-content: center; transition: background 0.1s, color 0.1s; font-family: 'Figtree', sans-serif; padding: 0; touch-action: manipulation; }
.dp-cell:hover:not(.dp-empty):not(.dp-selected) { background: #f1f5f9; }
.dp-empty { pointer-events: none; }
.dp-is-today { color: #3b82f6; font-weight: 700; }
.dp-selected { background: #3b82f6 !important; color: #fff !important; font-weight: 700; border-radius: 5px; }
.dp-month-grid { display: grid; grid-template-columns: repeat(3, 1fr); gap: 3px; padding: 2px 0 4px; }
.dp-month-cell { padding: 5px 2px; border: none; border-radius: 6px; background: transparent; font-size: 11px; font-weight: 500; color: #374151; cursor: pointer; font-family: 'Figtree', sans-serif; transition: background 0.1s; text-align: center; touch-action: manipulation; }
.dp-month-cell:hover { background: #f1f5f9; }
.dp-month-active { background: #3b82f6 !important; color: #fff !important; font-weight: 600; }
.dp-today-btn { width: 100%; margin-top: 6px; padding: 5px 0; border: 1px solid #e2e6ed; border-radius: 6px; background: transparent; color: #64748b; font-family: 'Figtree', sans-serif; font-size: 10.5px; font-weight: 500; cursor: pointer; transition: all 0.12s; touch-action: manipulation; }
.dp-today-btn:hover { background: #f1f5f9; border-color: #3b82f6; color: #3b82f6; }
.dp-today-btn.active { border-color: #3b82f6; color: #3b82f6; background: #eff6ff; }

/* ══ BOTTOM SHEETS ══ */
.sheet-overlay { position: fixed; inset: 0; z-index: 100; background: rgba(0,0,0,0.45); backdrop-filter: blur(3px); display: flex; align-items: flex-end; }
.sheet-panel { width: 100%; background: #fff; border-radius: 20px 20px 0 0; padding-bottom: env(safe-area-inset-bottom, 16px); max-height: 85dvh; display: flex; flex-direction: column; box-shadow: 0 -8px 32px rgba(0,0,0,0.18); }
.sheet-panel-tall { max-height: 80dvh; }
.sheet-handle { width: 36px; height: 4px; border-radius: 2px; background: #d0d5df; margin: 10px auto 0; }
.sheet-header { display: flex; align-items: center; gap: 10px; padding: 12px 20px 8px; flex-shrink: 0; border-bottom: 1px solid #edf0f4; }
.sheet-title { font-size: 16px; font-weight: 700; color: #1a1d27; flex: 1; }
.sheet-close { width: 30px; height: 30px; border-radius: 8px; border: 1px solid #e2e6ed; background: #f8f9fb; color: #64748b; display: flex; align-items: center; justify-content: center; cursor: pointer; touch-action: manipulation; }
.sheet-dp-body { padding: 8px 16px 16px; overflow-y: auto; }
.sheet-dp-body .dp-cell { height: 38px; font-size: 13px; }
.sheet-dp-body .dp-today-btn { padding: 10px; font-size: 13px; border-radius: 10px; margin-top: 8px; }
.sheet-list { flex: 1; overflow-y: auto; padding: 12px 14px; display: flex; flex-direction: column; gap: 8px; -webkit-overflow-scrolling: touch; }
.sheet-list .ucard { cursor: pointer; touch-action: manipulation; }
.sheet-list .ucard:active { transform: scale(0.98); }
.schedule-sheet-content { padding: 12px 20px 20px; flex: 1; overflow-y: auto; }
.sched-ev-title { font-size: 16px; font-weight: 700; color: #1a1d27; margin-bottom: 4px; }
.sched-ev-meta { font-size: 12px; color: #9ba3b2; display: flex; align-items: center; gap: 5px; margin-bottom: 16px; }
.sched-section-label { font-size: 10.5px; font-weight: 700; text-transform: uppercase; letter-spacing: 1.2px; color: #9ba3b2; margin-bottom: 8px; }
.sched-tech-list { display: flex; flex-wrap: wrap; gap: 8px; margin-bottom: 18px; }
.sched-tech-btn { display: flex; align-items: center; gap: 7px; padding: 8px 12px; border-radius: 10px; border: 1.5px solid #e2e6ed; background: #fff; cursor: pointer; touch-action: manipulation; font-family: 'Figtree', sans-serif; transition: all 0.12s; }
.sched-tech-avatar { width: 26px; height: 26px; border-radius: 6px; color: #fff; font-size: 9.5px; font-weight: 700; display: flex; align-items: center; justify-content: center; flex-shrink: 0; }
.sched-tech-name { font-size: 12.5px; font-weight: 600; color: #1a1d27; }
.sched-time-row { display: flex; gap: 8px; margin-bottom: 12px; }
.sched-select { flex: 1; padding: 10px 12px; border-radius: 10px; border: 1.5px solid #e2e6ed; background: #f8f9fb; font-family: 'Figtree', sans-serif; font-size: 14px; font-weight: 600; color: #1a1d27; appearance: none; cursor: pointer; }
.sched-preview { display: flex; align-items: center; gap: 6px; padding: 8px 12px; border-radius: 8px; background: #eff6ff; color: #3b82f6; font-size: 12.5px; font-weight: 500; margin-bottom: 16px; font-family: 'JetBrains Mono', monospace; }
.sched-confirm-btn { width: 100%; padding: 14px; border-radius: 12px; border: none; background: #3b82f6; color: #fff; font-family: 'Figtree', sans-serif; font-size: 14px; font-weight: 700; display: flex; align-items: center; justify-content: center; gap: 8px; cursor: pointer; touch-action: manipulation; transition: all 0.12s; }
.sched-confirm-btn:disabled { background: #c4cdd6; cursor: not-allowed; }
.sched-confirm-btn:not(:disabled):active { background: #2563eb; transform: scale(0.98); }

/* ══ DRAG GHOST ══ */
.drag-ghost {
  position: fixed; z-index: 9999; pointer-events: none;
  background: #1a1d27; color: #fff; border-radius: 10px;
  padding: 6px 14px; box-shadow: 0 8px 32px rgba(0,0,0,0.22);
  display: flex; flex-direction: column; gap: 1px; min-width: 120px; max-width: 220px;
}
.ghost-time { font-family: 'JetBrains Mono', monospace; font-size: 13px; font-weight: 600; color: #fff; white-space: nowrap; }
.ghost-title { font-size: 11px; color: #94a3b8; white-space: nowrap; overflow: hidden; text-overflow: ellipsis; max-width: 200px; }

/* ══ TRANSITIONS ══ */
.sheet-enter-active { transition: opacity 0.22s ease; }
.sheet-leave-active { transition: opacity 0.18s ease; }
.sheet-enter-from, .sheet-leave-to { opacity: 0; }
.sheet-enter-active .sheet-panel, .sheet-leave-active .sheet-panel { transition: transform 0.25s cubic-bezier(0.32, 0.72, 0, 1); }
.sheet-enter-from .sheet-panel, .sheet-leave-to .sheet-panel { transform: translateY(100%); }
.dropdown-enter-active { transition: opacity 0.15s, transform 0.15s; }
.dropdown-leave-active { transition: opacity 0.1s, transform 0.1s; }
.dropdown-enter-from, .dropdown-leave-to { opacity: 0; transform: translateY(-6px); }

@keyframes pending-block {
  0%, 100% { opacity: 1; }
  50%       { opacity: 0.30; }
}
</style>